import { Component, forwardRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ActionPriority, ActionState, ActionTopic, CustomPropertyDefinition } from '../../../model';
import { ActionDefinitionTypeService } from '../../../service/action-definition-type.service';
import { ActionDefinitionService } from '../../../service/action-definition.service';
import { AuthenticationService } from '../../../service/authentication.service';
import { CustomPropertyService, CustomPropertyType } from '../../../service/custom-property.service';
import { DateRangeService } from '../../../service/date-range.service';
import { FieldService } from '../../../service/field.service';
import { CapitalizePipe, LocalizationPipe } from '../../../shared/pipe';
import { AbstractAdvancedSearchComponent } from '../abstract-advanced-search.component';
import { ActionAdvancedSearchAddPropertiesDialog } from './action-advanced-search-add-properties-dialog.component';

@Component({
    selector: 'action-advanced-search',
    template: require('./action-advanced-search.component.html'),
    styles: [require('../thing-advanced-search/thing-advanced-search.component.css')],
})
export class ActionAdvancedSearchComponent extends AbstractAdvancedSearchComponent implements OnInit {

    @Input() isHistorical: boolean;

    @Input() periodDisabled: boolean;

    @ViewChild(ActionAdvancedSearchAddPropertiesDialog) addPropertiesDialog: ActionAdvancedSearchAddPropertiesDialog;

    private searchDataInitialized: boolean;
    private actionDefinitions: { value: string, label: string }[];
    private actionDefinitionTypes: { value: string, label: string }[];
    private topics: { value: string, label: string }[] = Object.values(ActionTopic).map(t => { return { label: this.capitalizePipe.transform(t), value: t } });
    private statuses: { value: string, label: string }[] = Object.values(ActionState).filter(s => s != ActionState.TODO).map(t => { return { label: this.capitalizePipe.transform(t), value: t } });
    private priorities: { value: string, label: string }[] = Object.values(ActionPriority).map(t => { return { label: this.capitalizePipe.transform(t), value: t } });

    static DEFAULT_SEARCHABLE_PROPERTIES: { name: string, label: string }[] = [
        { name: 'actionDefinition.type.label', label: 'actionDefinitionTypeProperty' },
        { name: 'actionDefinition.group', label: 'groupProperty' }
    ];

    defaultProperties = ActionAdvancedSearchComponent.DEFAULT_SEARCHABLE_PROPERTIES;

    constructor(
        @Inject(forwardRef(() => CapitalizePipe)) private capitalizePipe: CapitalizePipe,
        @Inject(forwardRef(() => LocalizationPipe)) localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => FieldService)) fieldService: FieldService,
        @Inject(forwardRef(() => ActionDefinitionService)) private actionDefinitionService: ActionDefinitionService,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => ActionDefinitionTypeService)) private actionDefinitionTypeService: ActionDefinitionTypeService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService
    ) { super(localizationPipe, fieldService) }

    ngOnInit(): void {
        this.localStorageKey = this.queryId || 'actionAdvancedSearchFieldsValues';
        this.savedFieldsValues = localStorage.getItem(this.localStorageKey) ? JSON.parse(localStorage.getItem(this.localStorageKey)) : null;
        this.properties = this.customPropertyService.getCustomPropertyDefinitionByType(CustomPropertyType.Action);
        const userFields = this.defaultSearchFields.concat(_.cloneDeep(this.authenticationService.getUser().actionsSearchFields) || []);
        this.searchFields = this.getSearchFields(userFields);
        this.getActionProperties();
        if (this.query && this.query.length) {
            this.getEncodedQueryFields();
        }
        if (this.queryFieldRef) {
            this.subscribeToQueryFieldRef();
        } else {
            if (this.savedFieldsValues || this.alwaysExpanded) {
                this.showHideAdvancedSearch().then(() => this.waitForAdvancedSearchRenderedAndPerformSearch());
            } else if (this.encodedQueryFields) {
                this.loadData(null, this.encodedQueryFields);
            }
        }
    }

    advancedSearch($event?): void {
        this.simpleSearchKey = null;
        const rawValue = this.advancedSearchEditor.getObjectValue();
        const key = this.advancedSearchBarEditor.getObjectValue()['key'];
        const rangeValue: { range: DateRange<moment.Moment>, rangeName: string } = rawValue['period'];
        const range: DateRange<moment.Moment> = rangeValue?.rangeName ? this.dateRangeService.getCustomDateRangeByName(rangeValue.rangeName)?.range : rangeValue?.range;
        const fields = {
            key: key,
            actionDefinitions: rawValue['actionDefinitions'],
            topics: rawValue['topics'],
            statuses: rawValue['statuses'],
            priorities: rawValue['priorities'],
            startTimestamp: range?.start?.valueOf(),
            endTimestamp: range?.end?.valueOf(),
            actionDefinitionTypes: rawValue['actionDefinitionTypes']
        };
        let fieldsToSave = {
            key: key,
            actionDefinitions: rawValue['actionDefinitions'],
            topics: rawValue['topics'],
            statuses: rawValue['statuses'],
            priorities: rawValue['priorities'],
            actionDefinitionTypes: rawValue['actionDefinitionTypes']
        };
        if (rangeValue?.rangeName) {
            fieldsToSave['rangeName'] = rangeValue?.rangeName;
        } else {
            fieldsToSave['startTimestamp'] = range?.start?.valueOf();
            fieldsToSave['endTimestamp'] = range?.end?.valueOf();
        }
        let encodedBody = Object.assign({}, fields, this.propertiesInputs ? this.propertiesInputs.getEncodedBody() : null);
        let fieldsToSaveBody = Object.assign({}, fieldsToSave, this.propertiesInputs ? this.propertiesInputs.getBody() : null);
        if (this.query && this.query.length) {
            encodedBody = Object.assign({}, encodedBody, this.encodedQueryFields);
            fieldsToSaveBody = this.removeQueryFields(fieldsToSaveBody);
        }
        this.handleSearchFieldSelectionInputs(encodedBody, fieldsToSaveBody);
        this.updateLocalStorage(fieldsToSaveBody);
        const encodedBodyValues = Object.keys(encodedBody).map(el => encodedBody[el]);
        if (encodedBodyValues.some(el => el != null)) {
            this.loadData(key, encodedBody);
        } else {
            this.loadData();
        }
        if ($event) {
            const eventObject = $event.currentTarget;
            eventObject.blur();
        }
    }

    protected initConfigurations(): Promise<void> {
        let advancedSearchBarConfiguration = [];
        advancedSearchBarConfiguration.push({ name: 'key', type: 'SEARCH', value: this.getValue('key') || this.simpleSearchKey });
        this.advancedSearchBarConfiguration = advancedSearchBarConfiguration;
        return this.getSearchData().then(() => {
            let advancedSearchConfiguration = [];
            if (!this.periodDisabled) {
                advancedSearchConfiguration.push({ name: 'period', 'label': 'periodProperty', type: 'PERIOD', value: this.getDateRange() || { range: null, rangeName: null }, defaultValue: this.isQueryField('startTimestamp') && this.isQueryField('endTimestamp') ? this.getDateRange() : null });
            }
            advancedSearchConfiguration.push({ name: 'topics', label: 'topicsProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.topics, value: this.getValue('topics'), multipleSelection: true, disabled: this.isQueryField('topics'), defaultValue: this.isQueryField('topics') ? this.getValue('topics') : null, placeholder: "All Topics" });
            if (this.actionDefinitionTypes.length > 0) {
                advancedSearchConfiguration.push({ name: 'actionDefinitionTypes', label: 'actionDefinitionTypesProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.actionDefinitionTypes, value: this.getValue('actionDefinitionTypes'), multipleSelection: true, disabled: this.isQueryField('actionDefinitionTypes'), defaultValue: this.isQueryField('actionDefinitionTypes') ? this.getValue('actionDefinitionTypes') : null, placeholder: "All Types" });
            }
            if (this.actionDefinitions.length > 1) {
                advancedSearchConfiguration.push({ name: 'actionDefinitions', label: 'actionsTabItem', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.actionDefinitions, value: this.getValue('actionDefinitions'), multipleSelection: true, disabled: this.isQueryField('actionDefinitions'), defaultValue: this.isQueryField('actionDefinitions') ? this.getValue('actionDefinitions') : null, placeholder: "All Actions" });
            }
            if (this.isHistorical) {
                advancedSearchConfiguration.push({ name: 'statuses', label: 'statusesProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.statuses, value: this.getValue('statuses'), multipleSelection: true, disabled: this.isQueryField('statuses'), defaultValue: this.isQueryField('statuses') ? this.getValue('statuses') : null, placeholder: "All Statuses" });
            }
            advancedSearchConfiguration.push({ name: 'priorities', label: 'prioritiesProperty', type: 'STRING', selectionMode: 'MAT_SELECTION', values: this.priorities, value: this.getValue('priorities'), multipleSelection: true, disabled: this.isQueryField('priorities'), defaultValue: this.isQueryField('priorities') ? this.getValue('priorities') : null, placeholder: "All Priorities" });
            this.fieldsPerRow = 2;
            this.advancedSearchConfiguration = advancedSearchConfiguration;
        });
    }

    private getSearchData(): Promise<any> {
        if (!this.searchDataInitialized) {
            this.searchDataInitialized = true;
            let promises = [];
            promises.push(this.actionDefinitionService.getRecursivelyAll().then(defs => this.actionDefinitions = defs.map(d => { return { value: d.id, label: d.label || d.name } }).sort((a, b) => (a.label > b.label) ? 1 : -1)).catch(() => this.actionDefinitions = []));
            promises.push(this.actionDefinitionTypeService.getAllActionDefinitionTypes().then(defs => this.actionDefinitionTypes = defs.map(d => { return { value: d.value, label: d.label } }).sort((a, b) => (a.label > b.label) ? 1 : -1)).catch(() => this.actionDefinitionTypes = []));
            return Promise.all(promises);
        }
        return Promise.resolve();
    }

    private getDateRange(): { range: DateRange<moment.Moment>, rangeName: string } {
        const rangeName = this.getValue('rangeName');
        if (rangeName) {
            return { range: null, rangeName: rangeName };
        } else {
            const start = this.getValue('startTimestamp');
            const end = this.getValue('endTimestamp');
            if (start && end) {
                return { range: new DateRange(moment(start), moment(end)), rangeName: null };
            }
        }
        return null;
    }

    getEncodedQueryFields(): void {
        let fields = [];
        this.query.forEach(el => {
            if (ActionAdvancedSearchComponent.DEFAULT_SEARCHABLE_PROPERTIES.some(defaultProp => defaultProp.name == el.property) || this.advancedSearchAddibleProperties.some(p => p.name == el.property || "properties." + p.name == el.property)) {
                let propDef: CustomPropertyDefinition = null;
                if (this.properties.some(prop => el.property == "properties." + prop.name)) {
                    propDef = this.properties.find(prop => el.property == "properties." + prop.name);
                }
                fields[el.property] = this.getQueryValueWithSuffixes(el.value, el.predicate, propDef);
            } else {
                fields[el.property] = el.value;
            }
        });
        this.encodedQueryFields = fields;
    }

    private getActionProperties(): void {
        this.advancedSearchAddibleProperties = [...ActionAdvancedSearchComponent.DEFAULT_SEARCHABLE_PROPERTIES];
        if (this.properties) {
            this.updateAdvancedSearchAddibleProperties(this.properties, '', null);
        }
    }

    openAddMorePropertiesDialog(): void {
        if (this.useExternalAddPropertiesDialog) {
            this.emitOpenExternalAddPropertiesDialogAction(this.advancedSearchAddibleProperties);
        } else {
            this.addPropertiesDialog.open();
        }
    }
}